import React, { useState, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    TextField,
    Grid,
    FormControlLabel,
    Switch,
    Stack,
    Card,
    CardActions,
    CardContent,
    useMediaQuery,
    Slide
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import DeleteIcon from "@mui/icons-material/Delete";
import { enUS } from "date-fns/locale";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CustomTimePicker from "./CustomTimePicker";
import DateFrequencySelector from "../../components/DateFrequencySelector";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import {
    blockedTimeFormDataAtom,
    recordIdAtom,
    scheduleInfoAtom,
    selectedBuIdAtom
} from "../../atoms/blockedTime";
import { userPageSnackbarAtom } from "../../atoms/usersTable";
import { useAtom } from "jotai";
import {
    mbxUserAtom,
    conformationDialogOpenAtom,
    conformationDialogContentAtom,
    confirmationFunctionAtom,
    conformationDialogTitleAtom,
    conformationDialogSubmitButtonAtom,
    conformationDialogIsSubmittingAtom
} from "../../atoms/atoms";
import { selectedProviderIdAtom } from "../../atoms/usersTable";
import { postApiWithStatusCode } from "../../modules/AmplifyServices";
import moment from "moment";
import { checkTimeBlockOverlap } from "../../utils/scheduleUtils";
import AddIcon from "@mui/icons-material/Add";
import { useTheme } from "@emotion/react";
import ArrowIcon from "../../images/ArrowIcon";
import _ from "lodash";

const SlideTransition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="left" ref={ref} {...props} />;
});

const BlockedTimeModal = ({
    open,
    onClose,
    buId,
    startDateInitial = null,
    endDateInitial = null,
    handleSave
}) => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"), {
        defaultMatches: true
    });
    const [formData] = useAtom(blockedTimeFormDataAtom);
    const [, setSelectedBuId] = useAtom(selectedBuIdAtom);
    const handleClose = () => {
        setSelectedBuId();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            fullScreen={!isDesktop}
            TransitionComponent={!isDesktop ? SlideTransition : "Fade"}
        >
            <DialogTitle
                variant="h3"
                sx={{
                    padding: "36px 36px 18px 36px",
                    gap: "1.125rem"
                }}
            >
                <ArrowIcon
                    sx={{
                        display: isDesktop && "none",
                        fontSize: "18px",
                        ":hover": {
                            cursor: "pointer"
                        }
                    }}
                    onClick={handleClose}
                />
                {formData ? "Edit blocked time " : "Add blocked time"}
            </DialogTitle>
            {open && (
                <BlockedTimeForm
                    handleCancel={handleClose}
                    startDateInitial={startDateInitial}
                    endDateInitial={endDateInitial}
                    handleSave={handleSave}
                />
            )}
        </Dialog>
    );
};

export default BlockedTimeModal;

const BlockedTimeForm = ({
    handleCancel,
    handleSave,
    startDateInitial,
    endDateInitial
}) => {
    const theme = useTheme();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [recordId] = useAtom(recordIdAtom);
    const [scheduleInfoConstructor] = useAtom(scheduleInfoAtom);
    const [formData] = useAtom(blockedTimeFormDataAtom);
    const [, setConformationDialogContent] = useAtom(
        conformationDialogContentAtom
    );
    const [, setConfirmationFunction] = useAtom(confirmationFunctionAtom);
    const [, setConformationDialogOpen] = useAtom(conformationDialogOpenAtom);
    const [, setConformationDialogTitle] = useAtom(conformationDialogTitleAtom);
    const [, setConformationDialogSubmitButton] = useAtom(
        conformationDialogSubmitButtonAtom
    );
    const [, setConformationDialogIsSubmitting] = useAtom(
        conformationDialogIsSubmittingAtom
    );
    const [, setUserPageSnackbar] = useAtom(userPageSnackbarAtom);
    const [openStartDate, setOpenStartDate] = useState(false);
    const [openEndDate, setOpenEndDate] = useState(false);
    const endTimeRefs = useRef([]);

    const timeRangeConstructor = () => {
        const now = new Date();

        // Set start time to the current hour plus one hour
        const startTime = new Date(now);
        startTime.setHours(now.getHours() + 1, 0, 0, 0);

        // Set end time to one hour after start time
        const endTime = new Date(startTime);
        endTime.setHours(startTime.getHours() + 1);

        return {
            id: recordId,
            scheduleinfo: scheduleInfoConstructor(),
            startTime: startTime.getTime(),
            endTime: endTime.getTime()
        };
    };

    const handleAddTimeBlock = () => {
        const lastEndTime = timeBlockFields.length
            ? timeBlockFields[timeBlockFields.length - 1].endTime
            : new Date().getHours() + 2;

        const newStartTime = new Date(lastEndTime).setHours(
            new Date(lastEndTime).getHours() + 1
        ); // One hour later than last end time

        const newTimeBlock = {
            id: recordId,
            scheduleinfo: scheduleInfoConstructor(),
            startTime: newStartTime,
            endTime: new Date(newStartTime).setHours(
                new Date(newStartTime).getHours() + 1
            ) // End time defualts to 1 hour after the start time
        };

        append(newTimeBlock);
    };
    const {
        buId,
        timeBlocks = [timeRangeConstructor()],
        notes = "",
        startDate = startDateInitial || new Date(),
        endDate = endDateInitial || new Date(),
        weekDays = moment(startDate).format("d"),
        weeksToRepeat = 1
    } = formData || {};
    const [{ companyId }] = useAtom(mbxUserAtom);
    const [providerId] = useAtom(selectedProviderIdAtom);
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"), {
        defaultMatches: true
    });
    const isAllDay = (blocks) => {
        const allDayStart = moment().startOf("day"); //midnight (00:00)
        const allDayEnd = moment().startOf("day").hours(23).minutes(59); //11:59 PM

        return _.some(blocks, (block) => {
            const blockStart = moment(block.startTime);
            const blockEnd = moment(block.endTime);

            return (
                blockStart.isSame(allDayStart, "minute") &&
                blockEnd.isSame(allDayEnd, "minute")
            );
        });
    };

    const form = useForm({
        defaultValues: {
            days: {
                weekDays: weekDays
                    .split("")
                    .map((stringNumber) => parseInt(stringNumber)),
                weeksToRepeat
            },
            startDate: moment(startDate),
            endDate: moment(endDate),
            timeBlocks,
            notes,
            blockAllDay: isAllDay(timeBlocks) || false
        }
    });
    const {
        handleSubmit,
        setValue,
        getValues,
        control,
        watch,
        formState,
        setError,
        clearErrors
    } = form;
    const {
        fields: timeBlockFields,
        append,
        remove
    } = useFieldArray({
        control,
        name: "timeBlocks"
    });
    const blockAllDay = watch("blockAllDay");
    const formStartDate = watch("startDate");
    const formEndDate = watch("endDate");

    const CustomTimePickerValidation = (timeBlocks) => {
        // Check if timeBlocks exists and is an array
        if (!timeBlocks || !Array.isArray(timeBlocks)) {
            console.error("Invalid timeBlocks data");
            return;
        }

        // Validation for startTime and endTime
        let hasError = false;

        for (const [index, timeBlock] of timeBlocks.entries()) {
            const { startTime, endTime } = timeBlock;

            if (endTime <= startTime) {
                setError(`timeBlocks.${index}.endTime`, {
                    type: "required",
                    message: "End time must be after start time"
                });
                hasError = true;

                // Scroll to the first error using ref
                if (endTimeRefs.current[index]) {
                    endTimeRefs.current[index].scrollIntoView({
                        behavior: "smooth",
                        block: "center"
                    });
                }
                break;
            }
        }
        return hasError;
    };

    //helper function to set the form state 'weekDays' when the date range is changed
    const setWeekDaysOnDateChange = (selectedStartDate, selectedEndDate) => {
        const startDate = moment.isMoment(selectedStartDate)
            ? selectedStartDate
            : moment(selectedStartDate);
        const endDate = moment.isMoment(selectedEndDate)
            ? selectedEndDate
            : moment(selectedEndDate);
        const normalizedStartDate = startDate.clone().startOf("day");
        const normalizedEndDate = endDate.clone().startOf("day");

        const weekDays = [];
        let currentDate = normalizedStartDate.clone();

        do {
            const dayOfWeek = currentDate.day();
            if (!weekDays.includes(dayOfWeek)) {
                weekDays.push(dayOfWeek);
            }
            if (weekDays.length === 7) {
                break;
            }
            currentDate.add(1, "day");
        } while (currentDate.isSameOrBefore(normalizedEndDate));

        setValue("days.weekDays", weekDays);
    };

    const onSubmit = async ({
        days: { weekDays, weeksToRepeat },
        startDate,
        endDate,
        timeBlocks,
        ...formData
    }) => {
        const customTimePickerError = CustomTimePickerValidation(timeBlocks);
        if (customTimePickerError) {
            return;
        }

        if (checkTimeBlockOverlap(timeBlocks)) {
            setUserPageSnackbar({
                open: true,
                message: `The selected times overlap. Please choose non-overlapping time ranges`,
                severity: "error"
            });
        }
        setIsSubmitting(true);
        try {
            //append additional data for API use
            const postData = {
                ...formData,
                weeksToRepeat,
                weekDays: weekDays.join(""),
                companyId,
                startDate: moment(startDate).format("YYYY-MM-DD"),
                endDate: moment(endDate).format("YYYY-MM-DD"),
                timeBlocks: timeBlocks.map(({ startTime, endTime }) => ({
                    startTime: moment(startTime).format("HH:mm"),
                    endTime: moment(endTime).format("HH:mm")
                })),
                providerId
            };
            if (!buId) {
                //create route
                const { statusCode, body: newRecord } =
                    await postApiWithStatusCode(
                        "userManagement",
                        "/user/blocked-time",
                        {
                            body: postData
                        }
                    );
                if (statusCode >= 200 && statusCode < 300) {
                    handleCancel();
                    setUserPageSnackbar({
                        open: true,
                        message: "Blocked Time has been successfully created",
                        severity: "success"
                    });
                    typeof handleSave === "function" && handleSave(newRecord);
                } else {
                    setUserPageSnackbar({
                        open: true,
                        message:
                            "Unexpected error: The blocked time was not created",
                        severity: "success"
                    });
                    throw new Error(
                        `Failed to create new user profile. Error: ${newRecord}`
                    );
                }
            } else {
                //edit route
                const { statusCode, body: updatedRecord } =
                    await postApiWithStatusCode(
                        "userManagement",
                        `/user/blocked-time`,
                        {
                            body: {
                                ...postData,
                                buId: `BU|${buId}`,
                                action: "EDIT"
                            }
                        }
                    );

                if (updatedRecord && statusCode >= 200 && statusCode < 300) {
                    handleCancel();
                    setUserPageSnackbar({
                        open: true,
                        message: "Blocked Time has been successfully updated",
                        severity: "success"
                    });
                    typeof handleSave === "function" &&
                        handleSave(updatedRecord);
                } else {
                    setUserPageSnackbar({
                        open: true,
                        message:
                            "Unexpected error: The blocked time was not updated",
                        severity: "success"
                    });
                    throw new Error(
                        `Failed to update user profile. Error: ${updatedRecord}`
                    );
                }
            }
        } catch (error) {
            console.log("Error submitting form data:", error);
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        setConformationDialogIsSubmitting(true);
        const { statusCode } = await postApiWithStatusCode(
            "userManagement",
            `/user/blocked-time`,
            {
                body: {
                    companyId,
                    providerId,
                    buId: `BU|${buId}`,
                    action: "DELETE"
                }
            }
        );
        if (statusCode >= 200 && statusCode < 300) {
            setUserPageSnackbar({
                open: true,
                message: `Blocked time has been successfully deleted`,
                severity: "success"
            });
        } else {
            console.log(statusCode);
            setUserPageSnackbar({
                open: true,
                message: `Unexpected Error: Unabled to delete blocked time`,
                severity: "error"
            });
        }
        setConformationDialogOpen(false);
        setConformationDialogIsSubmitting(false);
        handleCancel();
    };

    return (
        <form
            noValidate
            onSubmit={handleSubmit(onSubmit)}
            style={{
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                height: "100%"
            }}
        >
            <DialogContent>
                <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    adapterLocale={{
                        ...enUS,
                        options: { ...enUS.options, weekStartsOn: 1 }
                    }}
                >
                    <Grid container spacing={2.25} sx={{ marginTop: "0px" }}>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="startDate"
                                control={control}
                                render={({
                                    field: {
                                        onChange: onChangeFormHook,
                                        value: startDate
                                    }
                                }) => (
                                    <DatePicker
                                        autoFocus
                                        id="start-date"
                                        name="startDate"
                                        size="small"
                                        label="Start date"
                                        value={startDate}
                                        fullWidth
                                        inputFormat="MMM d, yyyy"
                                        onChange={(newValue) => {
                                            onChangeFormHook(newValue);
                                            newValue > getValues("endDate") &&
                                                setValue("endDate", newValue);
                                            setWeekDaysOnDateChange(
                                                newValue,
                                                formEndDate
                                            );
                                        }}
                                        open={openStartDate}
                                        onOpen={() => setOpenStartDate(true)}
                                        onClose={() => setOpenStartDate(false)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                inputProps={{
                                                    ...params.inputProps,
                                                    readOnly: true
                                                }}
                                                onClick={(e) =>
                                                    setOpenStartDate(true)
                                                }
                                                sx={{
                                                    "& .MuiInputAdornment-root":
                                                        {
                                                            display: "none"
                                                        },
                                                    "& fieldset": {
                                                        "& legend": {
                                                            width: "54px"
                                                        }
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="endDate"
                                control={control}
                                render={({
                                    field: {
                                        onChange: onChangeFormHook,
                                        value: endDate
                                    }
                                }) => (
                                    <DatePicker
                                        id="end-date"
                                        name="endDate"
                                        size="small"
                                        label="End date"
                                        value={endDate}
                                        minDate={getValues("startDate")}
                                        fullWidth
                                        inputFormat="MMM d, yyyy"
                                        onChange={(newValue) => {
                                            onChangeFormHook(newValue);
                                            setWeekDaysOnDateChange(
                                                formStartDate,
                                                newValue
                                            );
                                        }}
                                        open={openEndDate}
                                        onOpen={() => setOpenEndDate(true)}
                                        onClose={() => setOpenEndDate(false)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                inputProps={{
                                                    ...params.inputProps,
                                                    readOnly: true
                                                }}
                                                onClick={(e) =>
                                                    setOpenEndDate(true)
                                                }
                                                sx={{
                                                    "& .MuiInputAdornment-root":
                                                        {
                                                            display: "none"
                                                        },
                                                    "& fieldset": {
                                                        "& legend": {
                                                            width: "50px"
                                                        }
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid
                            container
                            item
                            xs={12}
                            sm={12}
                            sx={{
                                alignItems: "center",
                                justifyContent: "space-between"
                            }}
                        >
                            <Controller
                                name="days"
                                control={control}
                                render={({
                                    field: {
                                        onChange,
                                        value: { weekDays, weeksToRepeat }
                                    }
                                }) => (
                                    <DateFrequencySelector
                                        showToolTip={true}
                                        handleNumberSelectorChange={({
                                            target: { value: val }
                                        }) =>
                                            onChange({
                                                weeksToRepeat: val,
                                                weekDays
                                            })
                                        }
                                        handleDayButtonClick={(val) =>
                                            onChange({
                                                weeksToRepeat,
                                                weekDays: weekDays.includes(val)
                                                    ? weekDays.filter(
                                                          (element) =>
                                                              element !== val
                                                      )
                                                    : [...weekDays, val]
                                            })
                                        }
                                        selectedDays={weekDays}
                                        selectedNumber={weeksToRepeat}
                                        disabled={
                                            moment(formStartDate).format(
                                                "YYYY-MM-DD"
                                            ) ===
                                            moment(formEndDate).format(
                                                "YYYY-MM-DD"
                                            )
                                        }
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <FormControlLabel
                                control={
                                    <Controller
                                        name="blockAllDay"
                                        control={control}
                                        render={({
                                            field: {
                                                onChange: onChangeFormHook,
                                                value: blockAllDay
                                            }
                                        }) => (
                                            <Switch
                                                name="blockAllDay"
                                                checked={blockAllDay}
                                                value={blockAllDay}
                                                onChange={({
                                                    target: { checked }
                                                }) => {
                                                    onChangeFormHook(checked);
                                                    setValue("timeBlocks", [
                                                        checked
                                                            ? {
                                                                  startTime:
                                                                      new Date().setHours(
                                                                          0,
                                                                          0,
                                                                          0,
                                                                          0
                                                                      ),
                                                                  endTime:
                                                                      new Date().setHours(
                                                                          23,
                                                                          59,
                                                                          0,
                                                                          0
                                                                      )
                                                              }
                                                            : timeRangeConstructor()
                                                    ]);
                                                }}
                                            />
                                        )}
                                    />
                                }
                                label="All day"
                            />
                        </Grid>

                        {!blockAllDay && (
                            <Grid item xs={12} sm={12}>
                                <Card
                                    variant="outlined"
                                    sx={{ width: "100%", borderColor: "black" }}
                                >
                                    <CardContent>
                                        {timeBlockFields.map((item, index) => (
                                            <Stack key={item.scheduleinfo}>
                                                <Grid item xs={12} sm={6}>
                                                    <Controller
                                                        name={`timeBlocks.${index}.startTime`}
                                                        control={control}
                                                        render={({ field }) => {
                                                            const {
                                                                onChange:
                                                                    onChangeFormHook,
                                                                value: startTime
                                                            } = field;
                                                            return (
                                                                <>
                                                                    <CustomTimePicker
                                                                        id="start-time"
                                                                        name={`timeBlocks.${index}.startTime`}
                                                                        type="startTime"
                                                                        onChange={(
                                                                            timestamp
                                                                        ) => {
                                                                            clearErrors(
                                                                                `timeBlocks.${index}.endTime`
                                                                            );
                                                                            onChangeFormHook(
                                                                                timestamp
                                                                            );
                                                                        }}
                                                                        value={
                                                                            startTime
                                                                        }
                                                                    />
                                                                    {formState
                                                                        .errors
                                                                        ?.timeBlocks?.[
                                                                        index
                                                                    ]
                                                                        ?.endTime && (
                                                                        <Grid>
                                                                            <Typography variant="errorText">
                                                                                {
                                                                                    formState
                                                                                        .errors
                                                                                        .timeBlocks[
                                                                                        index
                                                                                    ]
                                                                                        .endTime
                                                                                        .message
                                                                                }
                                                                            </Typography>
                                                                        </Grid>
                                                                    )}
                                                                </>
                                                            );
                                                        }}
                                                    />
                                                </Grid>

                                                <Grid
                                                    item
                                                    xs={12}
                                                    sm={6}
                                                    display={"flex"}
                                                    alignItems={"center"}
                                                    gap={"10px"}
                                                >
                                                    <Controller
                                                        name={`timeBlocks.${index}.endTime`}
                                                        control={control}
                                                        render={({ field }) => {
                                                            const {
                                                                onChange:
                                                                    onChangeFormHook,
                                                                value: endTime
                                                            } = field;
                                                            return (
                                                                <Grid
                                                                    container
                                                                    sx={{
                                                                        display:
                                                                            "flex",
                                                                        flexDirection:
                                                                            "column"
                                                                    }}
                                                                >
                                                                    <CustomTimePicker
                                                                        id="end-time"
                                                                        name={`timeBlocks.${index}.endTime`}
                                                                        ref={(
                                                                            el
                                                                        ) => {
                                                                            endTimeRefs.current[
                                                                                index
                                                                            ] =
                                                                                el;
                                                                        }}
                                                                        type="endTime"
                                                                        error={
                                                                            !!formState
                                                                                .errors
                                                                                ?.timeBlocks?.[
                                                                                index
                                                                            ]
                                                                                ?.endTime
                                                                        }
                                                                        onChange={(
                                                                            timestamp
                                                                        ) => {
                                                                            clearErrors(
                                                                                `timeBlocks.${index}.endTime`
                                                                            );
                                                                            onChangeFormHook(
                                                                                timestamp
                                                                            );
                                                                        }}
                                                                        value={
                                                                            endTime
                                                                        }
                                                                    />
                                                                    {formState
                                                                        .errors
                                                                        ?.timeBlocks?.[
                                                                        index
                                                                    ]
                                                                        ?.endTime && (
                                                                        <Grid
                                                                            sx={{
                                                                                minHeight:
                                                                                    "21px"
                                                                            }}
                                                                        >
                                                                            <Typography>
                                                                                {" "}
                                                                            </Typography>
                                                                        </Grid>
                                                                    )}
                                                                </Grid>
                                                            );
                                                        }}
                                                    />
                                                    {index > 0 && (
                                                        <DeleteIcon
                                                            type="button"
                                                            variant="deleteIcon"
                                                            onClick={() => {
                                                                timeBlockFields.length >
                                                                    1 &&
                                                                    remove(
                                                                        index
                                                                    );
                                                            }}
                                                        />
                                                    )}
                                                </Grid>
                                            </Stack>
                                        ))}
                                    </CardContent>
                                    <CardActions>
                                        <Button
                                            variant="cancelButton"
                                            startIcon={<AddIcon />}
                                            onClick={handleAddTimeBlock}
                                        >
                                            <b> Add times</b>
                                        </Button>
                                    </CardActions>
                                </Card>
                            </Grid>
                        )}

                        <Grid item xs={12} sm={12}>
                            <Controller
                                name="notes"
                                control={control}
                                render={({
                                    field: {
                                        onChange: onChangeFormHook,
                                        value: notes
                                    }
                                }) => (
                                    <TextField
                                        id="notes"
                                        name="notes"
                                        label="Notes"
                                        size="small"
                                        multiline
                                        fullWidth
                                        variant="outlined"
                                        rows={3}
                                        sx={{
                                            "& fieldset": {
                                                "& legend": {
                                                    width: "36px"
                                                }
                                            }
                                        }}
                                        value={notes}
                                        onChange={onChangeFormHook}
                                    />
                                )}
                            />
                        </Grid>
                        {buId && (
                            <Grid item xs={12} sm={12}>
                                <Button
                                    onClick={() => {
                                        setConfirmationFunction(() =>
                                            handleDelete()
                                        );
                                        setConformationDialogContent(
                                            "Are you sure you want to delete this blocked time?"
                                        );
                                        setConformationDialogTitle(
                                            "Delete blocked time"
                                        );
                                        setConformationDialogSubmitButton(
                                            "Delete"
                                        );
                                        setConformationDialogOpen(true);
                                    }}
                                    variant="text"
                                    color="error"
                                    sx={{
                                        "&:hover": {
                                            backgroundColor:
                                                "transparent !important"
                                        }
                                    }}
                                    disableRipple
                                >
                                    <Typography
                                        variant="button"
                                        sx={{
                                            textTransform: "none",
                                            lineHeight: "16px"
                                        }}
                                    >
                                        {`Delete blocked time`}
                                    </Typography>
                                </Button>
                            </Grid>
                        )}
                    </Grid>
                </LocalizationProvider>
            </DialogContent>
            <DialogActions
                sx={{
                    display: "flex",
                    justifyContent: "flex-end"
                }}
            >
                <Grid
                    display={"flex"}
                    gap={"18px"}
                    width={!isDesktop ? "100%" : "auto"}
                >
                    {isDesktop && (
                        <Button onClick={handleCancel} variant="cancelButton">
                            Cancel
                        </Button>
                    )}

                    <LoadingButton
                        color="primary"
                        variant="contained"
                        type="submit"
                        loading={isSubmitting}
                        sx={{ width: !isDesktop ? "100%" : "auto" }}
                    >
                        Save
                    </LoadingButton>
                </Grid>
            </DialogActions>
        </form>
    );
};
